import React, { useCallback, useEffect, useState } from "react";
import { bondReadyAPI } from "../../services/api";
import { resolveLocalTestRunId } from "../../localE2eAuth";
import { qualityLabel, workflowLabel } from "../bondready/BondReadyPipeline";
import BondReadyShell, {
  Card,
  Pill,
  br,
  disclosureReasonLabel,
} from "../bondready/BondReadyShell";
import {
  Field,
  UNKNOWN,
  activityPresentation,
  bondRequestProjectContextRows,
  canonicalOwnerNextAction,
  customerFacingCurrentStageLabel,
  customerFacingPartnerDisplayName,
  dueLabel,
  humanDate,
  humanDateTime,
  humanizeEmbeddedInternalIdentifiers,
  informationAvailabilityLabel,
  informationItemLabel,
  informationRequestStatusLabel,
  informationResponseStateLabel,
  informationSourceAsOfLabel,
  informationSourceLabel,
  isCustomerFacingTimelineEvent,
  nextActionPresentation,
  opportunityRelationshipLabel,
  opportunityTrackerView,
  originLabel,
  ownerPresentation,
  readinessFromSnapshot,
  structuredFinancialAsOf,
  requestCountLabel,
  requestPrimaryDetailPresentation,
  requestStatusLabel,
  requestStageLabel,
  structuredFinancialThroughLabel,
  trackerStatusFromCanonicalOpportunity,
} from "../bondready/bondreadyPresentation";
import { getActuals } from "../../services/scenarioService";

export { default as BondReadyEntry } from "../bondready/BondReadyPipeline";

type ApiRecord = Record<string, any>;
const OPPORTUNITY_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const ATTACHMENT_SCAN_POLL_MS = 4000;
export const ATTACHMENT_SCAN_MAX_POLLS = 120;
const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export function attachmentScanPresentation(scanStatus?: string): { text: string; color: string } {
  switch (scanStatus) {
    case "CLEAN":
      return { text: "Malware scan passed", color: "#7ee2a8" };
    case "REJECTED":
      return { text: "Blocked: malware detected. Remove this file.", color: "#ff8a8a" };
    case "SCAN_FAILED":
      return { text: "Scan failed: the malware scan could not be completed. Remove it and upload again.", color: "#f0c674" };
    case "QUARANTINED":
      return { text: "Scanning for malware\u2026", color: "#9fb0c4" };
    default:
      return { text: "Not yet scanned", color: "#9fb0c4" };
  }
}

function value(input: any): React.ReactNode {
  if (input === null || input === undefined || input === "") return UNKNOWN;
  if (Array.isArray(input)) return input.length ? input.join(", ") : UNKNOWN;
  if (typeof input === "boolean") return input ? "Yes" : "No";
  return String(input);
}

function optional(payload: ApiRecord, key: string, input: string) {
  const cleaned = input.trim();
  if (cleaned) payload[key] = cleaned;
}

function optionalInstant(payload: ApiRecord, key: string, input: string) {
  const cleaned = input.trim();
  if (!cleaned) return;
  // Accept "YYYY-MM-DDTHH:mm" from OptionalDueInput; reject incomplete/invalid values.
  const parsed = new Date(cleaned);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Please enter a valid due date and time.");
  }
  payload[key] = parsed.toISOString();
}

/** Avoids incomplete browser-native datetime-local values (date without time). */
function OptionalDueInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const datePart = value.includes("T") ? value.split("T")[0] : (value.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || "");
  const timePart = value.includes("T") ? value.split("T")[1]?.slice(0, 5) || "" : "";
  return (
    <label style={{ color: "#9fb0c4", fontSize: 12 }} data-testid="bondready-optional-due">
      {label}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
        <input
          aria-label={`${label} date`}
          type="date"
          value={datePart}
          onChange={(event) => {
            const nextDate = event.target.value;
            if (!nextDate) {
              onChange("");
              return;
            }
            onChange(`${nextDate}T${timePart || "12:00"}`);
          }}
          style={br.field}
        />
        <input
          aria-label={`${label} time`}
          type="time"
          value={timePart || (datePart ? "12:00" : "")}
          disabled={!datePart}
          onChange={(event) => {
            if (!datePart) return;
            onChange(`${datePart}T${event.target.value || "12:00"}`);
          }}
          style={br.field}
        />
      </div>
    </label>
  );
}

function list(input: string): string[] {
  return input.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
}

function errorMessage(error: any): string {
  return error?.response?.data?.error || error?.message || "Request failed";
}

function opportunityLoadFailure(error: any): { title: string; message: string } {
  const status = Number(error?.response?.status || 0);
  if (status === 403) {
    return {
      title: "Access denied",
      message: "Your authenticated account is not permitted to open this BondReady request.",
    };
  }
  if (status === 404) {
    return {
      title: "Opportunity unavailable",
      message: "This BondReady request does not exist, was removed, or is not available to your organization.",
    };
  }
  return {
    title: "Unable to load opportunity",
    message: errorMessage(error),
  };
}

function structuredOwnerPresentation(record: ApiRecord): React.ReactNode {
  if (record.currentOwner) return ownerPresentation(record.currentOwner);
  if (record.currentOwnerFirebaseUid) return "Assigned user";
  return ownerPresentation(record.currentOwner);
}

function Input({ label, value: inputValue, onChange, required = false, type = "text" }: any) {
  return (
    <label style={{ color: "#9fb0c4", fontSize: 12 }}>
      {label}
      <input
        aria-label={label}
        type={type}
        required={required}
        value={inputValue}
        onChange={(event) => onChange(event.target.value)}
        style={br.field}
      />
    </label>
  );
}

function viewerAccessLabel(viewerRole: any): string | null {
  if (viewerRole === "AUTHORIZED_PARTNER") return "Authorized to review this opportunity";
  if (viewerRole === "OWNER") return "You own this opportunity";
  if (viewerRole === "CONSTRUCTPAY_OPERATOR") {
    return "ConstructPay operator ? workflow administration only";
  }
  return null;
}

function AuthorizationCard({ authorization }: { authorization?: ApiRecord }) {
  if (!authorization) return <div style={br.ticket}>{UNKNOWN}</div>;
  const decision = authorization.decision || authorization.state;
  return (
    <div style={br.ticket}>
      <div style={br.gridAuto}>
        <Field label="Authorization type">{value(authorization.authorizationType)}</Field>
        <Field label="Decision">{value(decision)}</Field>
        <Field label="Approved scope">{value(authorization.approvedScope)}</Field>
        <Field label="Exclusions">{value(authorization.exclusions)}</Field>
        <Field label="Package version">{value(authorization.releasePackageVersion)}</Field>
        <Field label="Package reference">{value(authorization.releasePackageReference)}</Field>
        <Field label="Evidence reference">{value(authorization.sourceEvidenceReference)}</Field>
        <Field label="Actor user UID">{value(authorization.actorFirebaseUid)}</Field>
        <Field label="Actor organization">{value(authorization.actorOrganizationName)}</Field>
        <Field label="Note">{value(authorization.note)}</Field>
      </div>
    </div>
  );
}

function BondRequestProjectContext({ request }: { request?: ApiRecord }) {
  const rows = bondRequestProjectContextRows(request?.projectContext);
  if (!rows.length) return null;
  return (
    <div style={{ ...br.ticket, marginTop: 12 }} data-testid="bondready-project-context">
      <h2 style={{ margin: "0 0 0.5rem", fontSize: "1rem" }}>Project</h2>
      <div style={br.gridAuto}>
        {rows.map((row) => (
          <React.Fragment key={row.key}>
            <Field label={row.label}>
              <span data-testid={`bondready-project-context-${row.key}`}>{row.value}</span>
            </Field>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ScenarioReference({ request }: { request: ApiRecord }) {
  if (request.scenarioAssumptionId === null || request.scenarioAssumptionId === undefined) {
    return <>{UNKNOWN}</>;
  }
  return (
    <a href="/cfo-insights/scenarios" style={{ color: "#7bb6ff" }}>
      {request.scenarioKey || "Scenario"} (ID {request.scenarioAssumptionId})
    </a>
  );
}

function RequestCard({
  request,
  opportunityId,
  viewerRole,
  onSaved,
}: {
  request: ApiRecord;
  opportunityId?: string;
  viewerRole?: string;
  onSaved?: () => Promise<void>;
}) {
  const title = request.requestType === "BOND" ? "Bonding" : request.requestType === "FUNDING" ? "Working Capital" : "Support request";
  const detail = requestPrimaryDetailPresentation(request);
  return (
    <div style={br.ticket} data-testid={`bondready-request-${request.id}`}>
      <h3 style={{ margin: "0 0 0.35rem", fontSize: "0.95rem" }}>
        {title}
        {request.projectContext?.projectName ? ` \u00B7 ${request.projectContext.projectName}` : ""}
      </h3>
      {detail.amountLabel ? (
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{detail.amountLabel}</div>
      ) : null}
      {detail.details ? (
        <div style={{ color: "#c8d4e4", marginBottom: 10, fontSize: detail.amountLabel ? 14 : 16, fontWeight: detail.amountLabel ? 400 : 600 }}>
          {detail.details}
        </div>
      ) : null}
      {!detail.amountLabel && !detail.details ? (
        <div style={{ fontSize: 13, color: "#9fb0c4", marginBottom: 10 }}>Amount not provided</div>
      ) : null}
      <div style={br.gridAuto}>
        <Field label="Status">{requestStatusLabel(request.status)}</Field>
        <Field label="Stage">{requestStageLabel(request.stage)}</Field>
        <Field label="Owner">{structuredOwnerPresentation(request)}</Field>
        <Field label="Next action">{value(request.nextAction)}</Field>
        <Field label="Due">{dueLabel(request.nextActionDueAt)}</Field>
        {request.outstandingInformation ? (
          <Field label="Outstanding information">
            {humanizeEmbeddedInternalIdentifiers(request.outstandingInformation)}
          </Field>
        ) : null}
        {(request.scenarioAssumptionId !== null && request.scenarioAssumptionId !== undefined) || request.scenarioKey ? (
          <Field label="Scenario reference"><ScenarioReference request={request} /></Field>
        ) : null}
      </div>
      {opportunityId && onSaved && request.requestType === "BOND" ? (
        <InformationRequestPanel
          opportunityId={String(opportunityId)}
          request={request}
          viewerRole={viewerRole}
          onSaved={onSaved}
        />
      ) : null}
      {opportunityId && onSaved && canMutateSupportRequest(viewerRole) ? (
        <RequestUpdateForm opportunityId={opportunityId} request={request} onSaved={onSaved} />
      ) : null}
    </div>
  );
}

function InformationRequestPanel({
  opportunityId,
  request,
  viewerRole,
  onSaved,
}: {
  opportunityId: string;
  request: ApiRecord;
  viewerRole?: string;
  onSaved: () => Promise<void>;
}) {
  type RequestMode = "EXISTING" | "MISSING_CONTACT" | "MISSING_OTHER";
  const [rows, setRows] = useState<ApiRecord[]>([]);
  const [busy, setBusyState] = useState(false);
  const busyRef = React.useRef(false);
  const setBusy = useCallback((next: boolean) => {
    busyRef.current = next;
    setBusyState(next);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [requestMode, setRequestMode] = useState<RequestMode>("MISSING_OTHER");
  const [customItemName, setCustomItemName] = useState("");
  const [requestText, setRequestText] = useState("");
  const [reRequestReason, setReRequestReason] = useState("");
  const [confirmReRequest, setConfirmReRequest] = useState(false);
  const [provideText, setProvideText] = useState("");
  const [responseRequirement, setResponseRequirement] = useState<"DOCUMENT" | "INFORMATION" | "EITHER">("DOCUMENT");
  // null = capability still loading; neither upload controls nor the unavailable notice render.
  const [attachmentsAvailable, setAttachmentsAvailable] = useState<boolean | null>(null);
  const [scanPoll, setScanPoll] = useState<{ key: string; attempts: number }>({ key: "", attempts: 0 });
  // Attachment scan/revoke endpoints authorize only the owning organization, not operators.
  const viewerOwnsAttachments = viewerRole === "OWNER";

  useEffect(() => {
    if (!viewerOwnsAttachments) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await (bondReadyAPI as any).getAttachmentCapability();
        if (!cancelled) setAttachmentsAvailable(response?.data?.available === true);
      } catch {
        if (!cancelled) setAttachmentsAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerOwnsAttachments]);

  const formatAttachmentSize = (bytes?: number) => {
    if (bytes == null || Number.isNaN(Number(bytes))) return "";
    const value = Number(bytes);
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openAttachment = async (attachment: ApiRecord, disposition: "inline" | "attachment") => {
    setBusy(true);
    setError(null);
    try {
      const response = await (bondReadyAPI as any).downloadInformationAttachment(
        opportunityId,
        attachment.id,
        disposition
      );
      const blob = new Blob([response.data], {
        type: attachment.contentType || "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      if (disposition === "inline") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = String(attachment.displayFilename || "document.pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const uploadPdf = async (infoRequest: ApiRecord, item: ApiRecord, file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const localTestRunId = resolveLocalTestRunId();
      await (bondReadyAPI as any).uploadInformationAttachment(
        opportunityId,
        infoRequest.id,
        item.id,
        file,
        localTestRunId
      );
      const input = document.querySelector(
        `[data-testid="bondready-info-choose-pdf-${item.id}"]`
      ) as HTMLInputElement | null;
      if (input) input.value = "";
      await reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const removeDraftAttachment = async (attachment: ApiRecord) => {
    setBusy(true);
    setError(null);
    try {
      await (bondReadyAPI as any).deleteInformationAttachment(opportunityId, attachment.id);
      await reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  // Mirrors the backend withdraw rule: the item reopens only when no shared document remains and
  // the response no longer meets its requirement, unless a newer request asks for the same item or
  // another open request is still waiting on it.
  const withdrawReopensItem = (infoRequest: ApiRecord, item: ApiRecord) => {
    const remainingShared = (item.attachments || []).filter((a: ApiRecord) => a.status === "SUBMITTED").length - 1;
    const requirement = String(item.responseRequirement || "EITHER");
    const hasText = !!String(item.responseText || "").trim()
      || !!String(item.responseEvidenceReference || "").trim();
    const requirementStillMet = requirement === "DOCUMENT"
      ? remainingShared > 0
      : requirement === "INFORMATION" || remainingShared > 0 || hasText;
    if (requirementStillMet || item.responseState !== "PROVIDED" || item.availabilityState !== "MISSING") {
      return false;
    }
    if (infoRequest.status === "CANCELLED") return false;
    const label = normalizeLabel(String(item.description || item.requestedItemLabel || ""));
    const requestedAt = Date.parse(String(infoRequest.requestedAt || ""));
    return !rows.some((other) => {
      if (other.id === infoRequest.id || other.status === "CANCELLED") return false;
      const newer = Date.parse(String(other.requestedAt || "")) > requestedAt;
      const open = other.status === "OPEN" || other.status === "RESPONSE_PROVIDED";
      return (other.items || []).some((otherItem: ApiRecord) => (
        otherItem.itemKey === item.itemKey
        && (item.itemKey !== "CUSTOM_REQUEST"
          || normalizeLabel(String(otherItem.description || otherItem.requestedItemLabel || "")) === label)
        && (newer || (open && otherItem.responseState === "PENDING"))
      ));
    });
  };

  const revokeSubmittedAttachment = async (infoRequest: ApiRecord, item: ApiRecord, attachment: ApiRecord) => {
    const outcome = withdrawReopensItem(infoRequest, item)
      ? "This item will reopen so you can upload a replacement."
      : "The response stays marked as provided.";
    const confirmed = window.confirm(
      `Withdraw ${attachment.displayFilename || "this document"}? Holmes Murphy will no longer be able to view or download it. ${outcome}`
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await (bondReadyAPI as any).revokeInformationAttachment(opportunityId, attachment.id);
      await reload();
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const selectedItemKey = requestMode === "EXISTING"
    ? "CURRENT_FINANCIAL_SNAPSHOT_AS_OF"
    : requestMode === "MISSING_CONTACT"
      ? "SURETY_APPLICATION_CONTACT_EMAIL"
      : "CUSTOM_REQUEST";

  const normalizeLabel = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

  const itemHistory = React.useMemo(() => {
    const openKeys = new Set<string>();
    const resolvedKeys = new Set<string>();
    const openCustomLabels = new Set<string>();
    const resolvedCustomLabels = new Set<string>();
    rows.forEach((infoRequest) => {
      const status = String(infoRequest.status || "");
      (infoRequest.items || []).forEach((item: ApiRecord) => {
        const key = String(item.itemKey || "");
        if (!key) return;
        const label = normalizeLabel(String(item.requestedItemLabel || item.description || ""));
        if (status === "OPEN" || status === "RESPONSE_PROVIDED") {
          if (item.responseState === "PENDING") {
            if (key === "CUSTOM_REQUEST") {
              if (label) openCustomLabels.add(label);
            } else {
              openKeys.add(key);
            }
          }
        }
        if (status === "RESOLVED") {
          if (key === "CUSTOM_REQUEST") {
            if (label) resolvedCustomLabels.add(label);
          } else {
            resolvedKeys.add(key);
          }
        }
      });
    });
    return { openKeys, resolvedKeys, openCustomLabels, resolvedCustomLabels };
  }, [rows]);

  const selectedCustomLabel = normalizeLabel(customItemName);
  const selectedHasOpen = selectedItemKey === "CUSTOM_REQUEST"
    ? !!(selectedCustomLabel && itemHistory.openCustomLabels.has(selectedCustomLabel))
    : itemHistory.openKeys.has(selectedItemKey);
  const selectedWasResolved = selectedItemKey === "CUSTOM_REQUEST"
    ? !!(selectedCustomLabel && itemHistory.resolvedCustomLabels.has(selectedCustomLabel))
    : itemHistory.resolvedKeys.has(selectedItemKey);
  const requiresReRequestIntent = selectedWasResolved && !selectedHasOpen;

  const applyModeDefaults = (mode: RequestMode) => {
    setRequestMode(mode);
    setConfirmReRequest(false);
    setReRequestReason("");
    if (mode === "EXISTING") {
      setCustomItemName("");
      setRequestText("Please confirm the as-of date of the current financial snapshot ConstructPay already holds.");
    } else if (mode === "MISSING_CONTACT") {
      setCustomItemName("");
      setRequestText("Please provide the surety application contact email. ConstructPay does not hold this.");
    } else {
      setRequestText("");
    }
  };

  const reloadGeneration = React.useRef(0);
  // Background reloads (scan polling) neither clear nor raise errors; user actions own the error state.
  const reload = useCallback(async (options?: { background?: boolean }) => {
    // Only the most recently started reload may apply its response.
    const generation = ++reloadGeneration.current;
    try {
      const response = await (bondReadyAPI as any).listInformationRequests(opportunityId, request.id);
      if (generation !== reloadGeneration.current) return;
      const nextRows = response.data || [];
      setRows(nextRows);
      if (!options?.background) setError(null);
      const resolvedExisting = nextRows.some((infoRequest: ApiRecord) => (
        infoRequest.status === "RESOLVED"
        && (infoRequest.items || []).some((item: ApiRecord) => (
          item.itemKey === "CURRENT_FINANCIAL_SNAPSHOT_AS_OF"
        ))
      ));
      const openExisting = nextRows.some((infoRequest: ApiRecord) => (
        (infoRequest.status === "OPEN" || infoRequest.status === "RESPONSE_PROVIDED")
        && (infoRequest.items || []).some((item: ApiRecord) => (
          item.itemKey === "CURRENT_FINANCIAL_SNAPSHOT_AS_OF"
          && item.responseState === "PENDING"
        ))
      ));
      if (resolvedExisting && !openExisting) {
        setRequestMode("MISSING_OTHER");
        setCustomItemName("");
        setRequestText("");
      }
    } catch (caught) {
      if (generation !== reloadGeneration.current || options?.background) return;
      setError(errorMessage(caught));
    }
  }, [opportunityId, request.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => () => {
    reloadGeneration.current += 1;
  }, []);

  const quarantinedAttachmentKey = React.useMemo(() => {
    if (!viewerOwnsAttachments) return "";
    const ids: string[] = [];
    rows.forEach((infoRequest) => {
      if (infoRequest.status !== "OPEN" && infoRequest.status !== "RESPONSE_PROVIDED") return;
      (infoRequest.items || []).forEach((item: ApiRecord) => {
        // Only items whose provide panel is shown, so every polled file has a visible status and recheck.
        if (item.availabilityState !== "MISSING" || item.responseState !== "PENDING") return;
        (item.attachments || []).forEach((attachment: ApiRecord) => {
          if (attachment.status === "DRAFT" && attachment.scanStatus === "QUARANTINED") {
            ids.push(String(attachment.id));
          }
        });
      });
    });
    return ids.sort().join(",");
  }, [rows, viewerOwnsAttachments]);

  // The poll budget belongs to the current quarantined set; any change to the set starts fresh.
  const scanPollAttempts = scanPoll.key === quarantinedAttachmentKey ? scanPoll.attempts : 0;
  const scanPollExhausted = !!quarantinedAttachmentKey && scanPollAttempts >= ATTACHMENT_SCAN_MAX_POLLS;

  useEffect(() => {
    if (!quarantinedAttachmentKey || scanPollAttempts >= ATTACHMENT_SCAN_MAX_POLLS) return undefined;
    const pollKey = quarantinedAttachmentKey;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (cancelled) return;
      let settled = false;
      for (const attachmentId of pollKey.split(",")) {
        if (cancelled) return;
        if (busyRef.current) {
          // A user mutation owns the panel and reloads on its own; retry later without spending budget.
          timer = setTimeout(tick, ATTACHMENT_SCAN_POLL_MS);
          return;
        }
        try {
          const response = await (bondReadyAPI as any).refreshInformationAttachmentScan(
            opportunityId,
            attachmentId
          );
          if (response?.data?.scanStatus && response.data.scanStatus !== "QUARANTINED") {
            settled = true;
          }
        } catch {
          // Stays quarantined and unavailable; the next poll retries within the budget.
        }
      }
      if (cancelled) return;
      if (settled && !busyRef.current) await reload({ background: true });
      if (cancelled) return;
      setScanPoll((previous) => ({
        key: pollKey,
        attempts: (previous.key === pollKey ? previous.attempts : 0) + 1,
      }));
    };
    timer = setTimeout(tick, ATTACHMENT_SCAN_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [quarantinedAttachmentKey, scanPollAttempts, opportunityId, reload]);

  const recheckQuarantined = async () => {
    const pollKey = quarantinedAttachmentKey;
    if (!pollKey) return;
    setBusy(true);
    setError(null);
    try {
      for (const attachmentId of pollKey.split(",")) {
        try {
          await (bondReadyAPI as any).refreshInformationAttachmentScan(opportunityId, attachmentId);
        } catch {
          // Still unavailable; the refreshed list shows the current state.
        }
      }
      await reload();
      setScanPoll({ key: pollKey, attempts: 0 });
    } finally {
      setBusy(false);
    }
  };

  const createRequest = async () => {
    setBusy(true);
    setError(null);
    try {
      if (requestMode === "MISSING_OTHER" && customItemName.trim().length < 3) {
        throw new Error("Enter a requested item name (for example, Owner personal financial statements).");
      }
      if (!requestText.trim()) {
        throw new Error("Enter reason / instructions for the contractor.");
      }
      if (selectedHasOpen) {
        throw new Error(
          `An open information request already exists for ${
            requestMode === "MISSING_OTHER"
              ? customItemName.trim()
              : informationItemLabel(selectedItemKey)
          }. Resolve or cancel it before requesting again.`
        );
      }
      if (requiresReRequestIntent && (!confirmReRequest || !reRequestReason.trim())) {
        throw new Error(
          "This item was previously resolved. Confirm re-request and provide a reason before submitting."
        );
      }

      let items: ApiRecord[];
      if (requestMode === "EXISTING") {
        items = [{
          itemKey: "CURRENT_FINANCIAL_SNAPSHOT_AS_OF",
          category: "FINANCIAL_SNAPSHOT",
          description: "As-of date of the current source-backed financial snapshot",
          responseRequirement: "INFORMATION",
        }];
      } else if (requestMode === "MISSING_CONTACT") {
        items = [{
          itemKey: "SURETY_APPLICATION_CONTACT_EMAIL",
          category: "SURETY_APPLICATION",
          description: "Surety application contact email - not stored in ConstructPay source systems",
          responseRequirement: "INFORMATION",
        }];
      } else {
        items = [{
          itemKey: "CUSTOM_REQUEST",
          category: "OTHER_INFORMATION",
          description: customItemName.trim(),
          responseRequirement,
        }];
      }

      await (bondReadyAPI as any).createInformationRequest(opportunityId, request.id, {
        requestText: requestText.trim(),
        reRequestReason: requiresReRequestIntent ? reRequestReason.trim() : null,
        items,
      });
      setConfirmReRequest(false);
      setReRequestReason("");
      setCustomItemName("");
      setRequestText("");
      await reload();
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const respondShare = async (infoRequest: ApiRecord, item: ApiRecord) => {
    setBusy(true);
    setError(null);
    try {
      await (bondReadyAPI as any).respondToInformationRequest(opportunityId, infoRequest.id, {
        responses: [
          {
            itemId: item.id,
            responseState: "AUTHORIZED_SHARE",
            responseText: "Verified existing ConstructPay financial snapshot as-of date. Authorized bounded share.",
            responseEvidenceReference: item.sourceEvidenceReference
              || `ui://information-response/authorize-share/${item.id}`,
          },
        ],
      });
      await reload();
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const respondProvide = async (infoRequest: ApiRecord, item: ApiRecord) => {
    setBusy(true);
    setError(null);
    try {
      const cleanDraftAttachments = (item.attachments || []).filter(
        (attachment: ApiRecord) => attachment.status === "DRAFT" && attachment.scanStatus === "CLEAN"
      );
      const attachmentIds = cleanDraftAttachments.map((attachment: ApiRecord) => attachment.id);
      const label = informationItemLabel(item.itemKey, item.requestedItemLabel || item.description);
      // Attachment-only responses carry no text, so withdrawing the last document can reopen an EITHER item.
      const responseBody = provideText.trim()
        || (attachmentIds.length > 0 ? null : `LOCAL FIXTURE ONLY ? provided response for: ${label}`);
      await (bondReadyAPI as any).respondToInformationRequest(opportunityId, infoRequest.id, {
        responses: [
          {
            itemId: item.id,
            responseState: "PROVIDED",
            responseText: responseBody,
            responseEvidenceReference: attachmentIds.length > 0
              ? null
              : `local-fixture://information-response/provided/${item.id}`,
            attachmentIds,
          },
        ],
      });
      setProvideText("");
      await reload();
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const isPartner = viewerRole === "AUTHORIZED_PARTNER";
  const isOwner = viewerRole === "OWNER" || viewerRole === "CONSTRUCTPAY_OPERATOR";

  return (
    <div style={{ marginTop: 12 }} data-testid="bondready-information-request-panel">
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Information request</div>
      {error ? <div role="alert" style={{ ...br.calloutRed, marginBottom: 8 }}>{error}</div> : null}
      {isPartner ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ ...br.callout, marginBottom: 8, fontSize: 12 }} data-testid="bondready-info-item-availability-guide">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>What you can request</div>
            <div>Already available in ConstructPay: Current financial snapshot date</div>
            <div>Missing / needs contractor input: Surety application contact email, or other information / documents</div>
          </div>
          <label style={{ display: "block", marginBottom: 6, color: "#9fb0c4", fontSize: 12 }}>
            Requested item
            <select
              value={requestMode}
              onChange={(event) => {
                const value = event.target.value;
                const mode: RequestMode = value === "EXISTING"
                  ? "EXISTING"
                  : value === "MISSING_CONTACT"
                    ? "MISSING_CONTACT"
                    : "MISSING_OTHER";
                applyModeDefaults(mode);
              }}
              style={{
                display: "block",
                width: "100%",
                marginTop: 4,
                background: "#0b1420",
                color: "#edf5ff",
                border: "1px solid #34445a",
                borderRadius: 6,
                padding: 8,
              }}
              data-testid="bondready-info-request-mode"
            >
              <option value="MISSING_OTHER">Other information or document</option>
              <option value="MISSING_CONTACT">Surety application contact email (missing)</option>
              <option value="EXISTING">Current financial snapshot date (available in ConstructPay)</option>
            </select>
          </label>
          {requestMode === "MISSING_OTHER" ? (
            <>
            <label style={{ display: "block", marginBottom: 6, color: "#9fb0c4", fontSize: 12 }}>
              Requested item name *
              <input
                value={customItemName}
                onChange={(event) => setCustomItemName(event.target.value)}
                placeholder="e.g. Owner personal financial statements"
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 4,
                  background: "#0b1420",
                  color: "#edf5ff",
                  border: "1px solid #34445a",
                  borderRadius: 6,
                  padding: 8,
                }}
                data-testid="bondready-info-custom-item-name"
              />
            </label>
            <label style={{ display: "block", marginBottom: 6, color: "#9fb0c4", fontSize: 12 }}>
              Response requirement *
              <select
                value={responseRequirement}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "DOCUMENT" || value === "INFORMATION" || value === "EITHER") {
                    setResponseRequirement(value);
                  }
                }}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 4,
                  background: "#0b1420",
                  color: "#edf5ff",
                  border: "1px solid #34445a",
                  borderRadius: 6,
                  padding: 8,
                }}
                data-testid="bondready-info-response-requirement"
              >
                <option value="DOCUMENT">Document attachment required</option>
                <option value="INFORMATION">Text / information only</option>
                <option value="EITHER">Either text or document</option>
              </select>
            </label>
            </>
          ) : null}
          {selectedHasOpen ? (
            <div role="status" style={{ ...br.calloutWarn, marginBottom: 8 }} data-testid="bondready-info-request-open-block">
              An open request already exists for {
                requestMode === "MISSING_OTHER"
                  ? (customItemName.trim() || "this item")
                  : informationItemLabel(selectedItemKey)
              }. Resolve it before creating another.
            </div>
          ) : null}
          {requiresReRequestIntent ? (
            <div style={{ ...br.calloutWarn, marginBottom: 8 }} data-testid="bondready-info-request-rerequest-warn">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {requestMode === "MISSING_OTHER"
                  ? (customItemName.trim() || "This item")
                  : informationItemLabel(selectedItemKey)} was previously resolved.
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "#edf5ff" }}>
                <input
                  type="checkbox"
                  checked={confirmReRequest}
                  onChange={(event) => setConfirmReRequest(event.target.checked)}
                  data-testid="bondready-info-rerequest-confirm"
                />
                <span>I intentionally want to re-request this resolved item</span>
              </label>
              <label style={{ display: "block", marginTop: 8, color: "#9fb0c4", fontSize: 12 }}>
                Re-request reason
                <textarea
                  value={reRequestReason}
                  onChange={(event) => setReRequestReason(event.target.value)}
                  rows={2}
                  style={{
                    width: "100%",
                    marginTop: 4,
                    background: "#0b1420",
                    color: "#edf5ff",
                    border: "1px solid #34445a",
                    borderRadius: 6,
                    padding: 8,
                  }}
                  data-testid="bondready-info-rerequest-reason"
                />
              </label>
            </div>
          ) : null}
          <label style={{ display: "block", marginBottom: 6, color: "#9fb0c4", fontSize: 12 }}>
            Reason / instructions *
            <textarea
              value={requestText}
              onChange={(event) => setRequestText(event.target.value)}
              rows={2}
              placeholder="e.g. Please provide current PFS for the owners."
              style={{
                width: "100%",
                marginTop: 4,
                background: "#0b1420",
                color: "#edf5ff",
                border: "1px solid #34445a",
                borderRadius: 6,
                padding: 8,
              }}
              data-testid="bondready-info-request-text"
            />
          </label>
          <button
            type="button"
            style={{ ...br.btn, ...br.btnPrimary }}
            disabled={
              busy
              || selectedHasOpen
              || !requestText.trim()
              || (requestMode === "MISSING_OTHER" && customItemName.trim().length < 3)
              || (requiresReRequestIntent && (!confirmReRequest || !reRequestReason.trim()))
            }
            onClick={createRequest}
            data-testid="bondready-info-request-create"
          >
            {busy ? "Submitting?" : requiresReRequestIntent ? "Re-request information" : "Request information"}
          </button>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <div style={br.muted}>No information requests on this Bond request yet.</div>
      ) : (
        rows.map((infoRequest) => (
          <div
            key={String(infoRequest.id)}
            style={{ ...br.callout, marginTop: 8 }}
            data-testid={`bondready-info-request-${infoRequest.id}`}
          >
            <div style={br.gridAuto}>
              <Field label="Status">{informationRequestStatusLabel(infoRequest.status)}</Field>
              <Field label="Requested at">{humanDateTime(infoRequest.requestedAt)}</Field>
            </div>
            {(infoRequest.items || []).map((item: ApiRecord) => {
              const itemLabel = informationItemLabel(
                item.itemKey,
                item.requestedItemLabel || item.description
              );
              return (
              <div key={String(item.id)} style={{ marginTop: 8 }} data-testid={`bondready-info-item-${item.id}`}>
                <div style={br.gridAuto}>
                  <Field label="Requested item">{itemLabel}</Field>
                  <Field label="Availability">{informationAvailabilityLabel(item.availabilityState)}</Field>
                  <Field label="Response">{informationResponseStateLabel(item.responseState)}</Field>
                  {infoRequest.requestText ? (
                    <Field label={isOwner ? "Holmes Murphy asks" : "Instructions"}>
                      {value(infoRequest.requestText)}
                    </Field>
                  ) : null}
                  {item.contractorActionHint ? (
                    <Field label="Action">{value(item.contractorActionHint)}</Field>
                  ) : null}
                  {isOwner && item.availabilityState === "MISSING" && item.responseState === "PENDING" ? (
                    <Field label="Your action">
                      Provide {itemLabel}. ConstructPay does not already have this information.
                    </Field>
                  ) : null}
                  {informationSourceLabel(item.sourceSystem, item.sourceLabel) ? (
                    <Field label="Source">
                      {informationSourceLabel(item.sourceSystem, item.sourceLabel)}
                    </Field>
                  ) : null}
                  {item.sourceSnapshotAsOf ? (
                    <Field label="Source as of">{informationSourceAsOfLabel(item.sourceSnapshotAsOf)}</Field>
                  ) : null}
                  {viewerRole === "CONSTRUCTPAY_OPERATOR" && item.sourceEvidenceReference ? (
                    <Field label="Source evidence">{value(item.sourceEvidenceReference)}</Field>
                  ) : null}
                  {viewerRole === "CONSTRUCTPAY_OPERATOR" && item.sourceSystem ? (
                    <Field label="Provenance (internal)">{value(item.sourceSystem)}</Field>
                  ) : null}
                  {item.responseText ? <Field label="Response note">{value(item.responseText)}</Field> : null}
                  {isPartner && item.responseState === "PROVIDED" && infoRequest.owningOrganizationName ? (
                    <Field label="Provided by">{value(infoRequest.owningOrganizationName)}</Field>
                  ) : null}
                  {viewerRole === "CONSTRUCTPAY_OPERATOR" && item.responseEvidenceReference ? (
                    <Field label="Response evidence">{value(item.responseEvidenceReference)}</Field>
                  ) : null}
                  {item.respondedAt ? (
                    <Field label="Responded at">{humanDateTime(item.respondedAt)}</Field>
                  ) : null}
                </div>
                {isOwner
                  && (infoRequest.status === "OPEN" || infoRequest.status === "RESPONSE_PROVIDED")
                  && item.availabilityState === "AVAILABLE_EXISTING"
                  && item.responseState === "PENDING" ? (
                  <button
                    type="button"
                    style={{ ...br.btn, ...br.btnPrimary, marginTop: 8 }}
                    disabled={busy}
                    onClick={() => respondShare(infoRequest, item)}
                    data-testid={`bondready-info-authorize-share-${item.id}`}
                  >
                    Verify and authorize share
                  </button>
                ) : null}
                {isOwner
                  && (infoRequest.status === "OPEN" || infoRequest.status === "RESPONSE_PROVIDED")
                  && item.availabilityState === "MISSING"
                  && item.responseState === "PENDING" ? (
                  (() => {
                    const requirement = String(item.responseRequirement || "EITHER");
                    const draftAttachments = (item.attachments || []).filter(
                      (a: ApiRecord) => a.status === "DRAFT"
                    );
                    const cleanDrafts = draftAttachments.filter(
                      (a: ApiRecord) => a.scanStatus === "CLEAN"
                    );
                    const scanningDrafts = draftAttachments.filter(
                      (a: ApiRecord) => a.scanStatus === "QUARANTINED"
                    );
                    // Submitting only shares the listed drafts; failed ones must be removed first so they
                    // are not left behind as hidden drafts once the item is provided.
                    const blockedDrafts = draftAttachments.filter(
                      (a: ApiRecord) => a.scanStatus !== "CLEAN" && a.scanStatus !== "QUARANTINED"
                    );
                    const needsDocument = requirement === "DOCUMENT";
                    const needsInformation = requirement === "INFORMATION";
                    const canSubmitDocument = !needsDocument || cleanDrafts.length > 0;
                    const canSubmitInformation = !needsInformation || !!provideText.trim();
                    const canSubmitEither = requirement !== "EITHER"
                      || cleanDrafts.length > 0
                      || !!provideText.trim();
                    const submitEnabled = !busy
                      && scanningDrafts.length === 0
                      && blockedDrafts.length === 0
                      && canSubmitDocument
                      && canSubmitInformation
                      && canSubmitEither;
                    const fileInputId = `bondready-info-choose-pdf-${item.id}`;
                    return (
                  <div style={{ marginTop: 8 }} data-testid={`bondready-info-provide-panel-${item.id}`}>
                    <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                      Provide requested information
                    </div>
                    {(needsDocument || requirement === "EITHER") && attachmentsAvailable === false ? (
                      <div
                        role="status"
                        style={{ color: "#f0c674", fontSize: 12, marginBottom: 8 }}
                        data-testid={`bondready-info-upload-unavailable-${item.id}`}
                      >
                        Secure document upload is not available yet.
                      </div>
                    ) : null}
                    {(needsDocument || requirement === "EITHER") && attachmentsAvailable === true ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                      <input
                        id={fileInputId}
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: "none" }}
                        data-testid={fileInputId}
                        onChange={(event) => {
                          const file = event.target.files && event.target.files[0]
                            ? event.target.files[0]
                            : null;
                          uploadPdf(infoRequest, item, file);
                        }}
                      />
                      <button
                        type="button"
                        style={{ ...br.btn }}
                        disabled={busy}
                        onClick={() => {
                          const input = document.getElementById(fileInputId) as HTMLInputElement | null;
                          input?.click();
                        }}
                        data-testid={`bondready-info-choose-pdf-btn-${item.id}`}
                      >
                        Choose PDF
                      </button>
                      {needsDocument ? (
                        <span style={{ color: "#9fb0c4", fontSize: 12 }}>
                          Document attachment required
                        </span>
                      ) : null}
                    </div>
                    ) : null}
                    <label style={{ display: "block", marginBottom: 8, color: "#9fb0c4", fontSize: 12 }}>
                      {needsDocument ? "Optional note" : "Response note"}
                      <textarea
                        value={provideText}
                        onChange={(event) => setProvideText(event.target.value)}
                        rows={2}
                        placeholder={needsDocument
                          ? `Optional note for: ${itemLabel}`
                          : `Enter or paste the response for: ${itemLabel}`}
                        style={{
                          width: "100%",
                          marginTop: 4,
                          background: "#0b1420",
                          color: "#edf5ff",
                          border: "1px solid #34445a",
                          borderRadius: 6,
                          padding: 8,
                        }}
                        data-testid="bondready-info-provide-text"
                      />
                    </label>
                    {draftAttachments.length > 0 ? (
                      <div style={{ marginBottom: 8 }} data-testid={`bondready-info-draft-attachments-${item.id}`}>
                        <div style={{ fontSize: 12, color: "#9fb0c4", marginBottom: 4 }}>Attached files</div>
                        {draftAttachments.map((attachment: ApiRecord) => (
                            <div
                              key={String(attachment.id)}
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                flexWrap: "wrap",
                                marginBottom: 4,
                                fontSize: 13,
                              }}
                              data-testid={`bondready-info-draft-attachment-${attachment.id}`}
                            >
                              <span>{value(attachment.displayFilename)}</span>
                              <span style={{ color: "#9fb0c4" }}>
                                PDF{" \u00B7 "}{formatAttachmentSize(attachment.fileSizeBytes)}
                              </span>
                              <span
                                role="status"
                                style={{ color: attachmentScanPresentation(attachment.scanStatus).color, fontSize: 12 }}
                                data-testid={`bondready-info-attachment-scan-${attachment.id}`}
                              >
                                <span style={visuallyHidden}>{`${attachment.displayFilename || "Document"}: `}</span>
                                {attachmentScanPresentation(attachment.scanStatus).text}
                              </span>
                              <button
                                type="button"
                                style={{ ...br.btn }}
                                disabled={busy}
                                onClick={() => removeDraftAttachment(attachment)}
                                data-testid={`bondready-info-remove-attachment-${attachment.id}`}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                      </div>
                    ) : null}
                    {needsDocument && draftAttachments.length === 0 && attachmentsAvailable === true ? (
                      <div
                        role="status"
                        style={{ color: "#f0c674", fontSize: 12, marginBottom: 8 }}
                        data-testid={`bondready-info-document-required-${item.id}`}
                      >
                        Attach at least one PDF before submitting.
                      </div>
                    ) : null}
                    {scanningDrafts.length > 0 && !scanPollExhausted ? (
                      <div
                        style={{ color: "#9fb0c4", fontSize: 12, marginBottom: 8 }}
                        data-testid={`bondready-info-scan-pending-${item.id}`}
                      >
                        Files are shared only after the malware scan passes. Submit becomes available when scanning finishes.
                      </div>
                    ) : null}
                    {scanningDrafts.length > 0 && scanPollExhausted ? (
                      <div
                        style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}
                        data-testid={`bondready-info-scan-stalled-${item.id}`}
                      >
                        <span role="status" style={{ color: "#f0c674", fontSize: 12 }}>
                          Still scanning {"\u2014"} check again.
                        </span>
                        <button
                          type="button"
                          style={{ ...br.btn }}
                          disabled={busy}
                          onClick={recheckQuarantined}
                          data-testid={`bondready-info-scan-recheck-${item.id}`}
                        >
                          Check again
                        </button>
                      </div>
                    ) : null}
                    {blockedDrafts.length > 0 ? (
                      <div
                        style={{ color: "#f0c674", fontSize: 12, marginBottom: 8 }}
                        data-testid={`bondready-info-scan-blocked-${item.id}`}
                      >
                        Remove files that did not pass the malware scan before submitting.
                      </div>
                    ) : null}
                    <button
                      type="button"
                      style={{ ...br.btn, ...br.btnPrimary }}
                      disabled={!submitEnabled}
                      onClick={() => respondProvide(infoRequest, item)}
                      data-testid={`bondready-info-provide-missing-${item.id}`}
                    >
                      Submit response
                    </button>
                  </div>
                    );
                  })()
                ) : null}
                {(item.attachments || []).filter((a: ApiRecord) => a.status === "SUBMITTED").length > 0 ? (
                  <div style={{ marginTop: 8 }} data-testid={`bondready-info-submitted-attachments-${item.id}`}>
                    <div style={{ fontSize: 12, color: "#9fb0c4", marginBottom: 4 }}>Attachments</div>
                    {(item.attachments || [])
                      .filter((a: ApiRecord) => a.status === "SUBMITTED")
                      .map((attachment: ApiRecord) => (
                        <div
                          key={String(attachment.id)}
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                            marginBottom: 4,
                            fontSize: 13,
                          }}
                          data-testid={`bondready-info-submitted-attachment-${attachment.id}`}
                        >
                          <span>{value(attachment.displayFilename)}</span>
                          <span style={{ color: "#9fb0c4" }}>
                            PDF{" \u00B7 "}{formatAttachmentSize(attachment.fileSizeBytes)}
                          </span>
                          <button
                            type="button"
                            style={{ ...br.btn }}
                            disabled={busy}
                            onClick={() => openAttachment(attachment, "inline")}
                            data-testid={`bondready-info-view-attachment-${attachment.id}`}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            style={{ ...br.btn }}
                            disabled={busy}
                            onClick={() => openAttachment(attachment, "attachment")}
                            data-testid={`bondready-info-download-attachment-${attachment.id}`}
                          >
                            Download
                          </button>
                          {viewerOwnsAttachments ? (
                            <button
                              type="button"
                              style={{ ...br.btn }}
                              disabled={busy}
                              onClick={() => revokeSubmittedAttachment(infoRequest, item, attachment)}
                              data-testid={`bondready-info-revoke-attachment-${attachment.id}`}
                            >
                              Withdraw
                            </button>
                          ) : null}
                        </div>
                      ))}
                    {item.respondedAt ? (
                      <div style={{ fontSize: 12, color: "#9fb0c4", marginTop: 4 }}>
                        Provided {humanDateTime(item.respondedAt)}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

function DataQualityNotice({ status }: { status?: string }) {
  const normalized = status || "SOURCE_PENDING";
  const copy: Record<string, string> = {
    CURRENT: "Structured financial data through the latest ingested snapshot date is on file for BondReady.",
    STALE: "Source data is stale; confirm it before taking the next action.",
    CONFLICT: "Source data conflict requires reconciliation before action.",
    SOURCE_PENDING: "Source status is unknown / pending.",
  };
  const message = copy[normalized] || `Unrecognized source status: ${normalized}`;
  const toneStyle = normalized === "CONFLICT" ? br.calloutRed : normalized === "STALE" ? br.calloutWarn : br.callout;
  return <div role="status" style={{ ...toneStyle, marginTop: 10 }}>{message}</div>;
}

function useNarrowBondReadyViewport(maxWidth = 720) {
  const [narrow, setNarrow] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = () => setNarrow(media.matches);
    onChange();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, [maxWidth]);
  return narrow;
}

function OpportunityLifecycleTracker({
  opportunity,
}: {
  opportunity: ApiRecord;
}) {
  const trackerStatus = trackerStatusFromCanonicalOpportunity(opportunity);
  const tracker = opportunityTrackerView(trackerStatus);
  const canonical = canonicalOwnerNextAction(opportunity);
  const currentStageLabel = customerFacingCurrentStageLabel(opportunity);
  const narrow = useNarrowBondReadyViewport(720);
  return (
    <Card
      title="Track this opportunity"
      subtitle="Where the bonding / capital opportunity is now ? completed, current, and still ahead."
      badge={<Pill tone="blue">Lifecycle</Pill>}
      testId="bondready-lifecycle-tracker"
      style={br.sectionPad}
    >
      {tracker.declined && (
        <div role="status" style={{ ...br.calloutWarn, marginBottom: 12 }}>
          This opportunity was declined. Historical stages below remain for context.
        </div>
      )}
      <ol
        aria-label="Opportunity lifecycle stages"
        data-testid="bondready-pizza-tracker"
        data-layout={narrow ? "vertical" : "horizontal"}
        style={narrow ? {
          listStyle: "none",
          margin: 0,
          padding: "4px 0",
          display: "flex",
          flexDirection: "column",
          gap: 0,
        } : {
          listStyle: "none",
          margin: 0,
          padding: "8px 4px 4px",
          display: "grid",
          gridTemplateColumns: `repeat(${tracker.stages.length}, minmax(0, 1fr))`,
          gap: 0,
          alignItems: "start",
        }}
      >
        {tracker.stages.map((stage, idx) => {
          const completed = stage.state === "completed";
          const current = stage.state === "current";
          const circleBorder = completed ? "#3b8d63" : current ? "#7bb6ff" : "#34445a";
          const circleBg = completed ? "#102b21" : current ? "#10243a" : "#0b1420";
          const circleColor = completed ? "#b8ffd0" : current ? "#bedcff" : "#64748b";
          const labelColor = completed ? "#cbd5e1" : current ? "#edf5ff" : "#64748b";
          const connectorActive = idx <= tracker.currentIndex && !tracker.declined;
          if (narrow) {
            return (
              <li
                key={stage.key}
                aria-current={current ? "step" : undefined}
                data-testid={`bondready-tracker-stage-${stage.key}`}
                data-state={stage.state}
                style={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "34px minmax(0, 1fr)",
                  columnGap: 12,
                  alignItems: "start",
                  padding: "8px 0",
                }}
              >
                {idx < tracker.stages.length - 1 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 15,
                      top: 42,
                      bottom: -8,
                      width: 3,
                      background: connectorActive && idx < tracker.currentIndex ? "#3b8d63" : "#243246",
                      zIndex: 0,
                    }}
                  />
                )}
                <span
                  aria-hidden="true"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "inline-flex",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${circleBorder}`,
                    background: circleBg,
                    color: circleColor,
                    fontWeight: 800,
                    fontSize: 13,
                    boxShadow: current ? "0 0 0 4px rgba(123,182,255,.18)" : undefined,
                  }}
                >
                  {completed ? "✓" : idx + 1}
                </span>
                <div style={{ minWidth: 0, paddingTop: 2 }}>
                  <div style={{ fontSize: 14, fontWeight: current ? 800 : 650, color: labelColor, lineHeight: 1.3 }}>
                    {stage.label}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: circleColor }}>
                    {completed ? "Completed" : current ? "Current" : "Pending"}
                  </div>
                </div>
              </li>
            );
          }
          return (
            <li
              key={stage.key}
              aria-current={current ? "step" : undefined}
              data-testid={`bondready-tracker-stage-${stage.key}`}
              data-state={stage.state}
              style={{ position: "relative", textAlign: "center", padding: "0 4px" }}
            >
              {idx > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 16,
                    right: "50%",
                    width: "100%",
                    height: 3,
                    background: connectorActive ? "#3b8d63" : "#243246",
                    zIndex: 0,
                  }}
                />
              )}
              <span
                aria-hidden="true"
                data-testid={completed ? `bondready-tracker-stage-${stage.key}-complete-icon` : undefined}
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "inline-flex",
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${circleBorder}`,
                  background: circleBg,
                  color: circleColor,
                  fontWeight: 800,
                  fontSize: 13,
                  boxShadow: current ? "0 0 0 4px rgba(123,182,255,.18)" : undefined,
                }}
              >
                {completed ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M3.5 8.5L6.5 11.5L12.5 4.5"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  idx + 1
                )}
              </span>
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: current ? 800 : 600, color: labelColor, lineHeight: 1.25 }}>
                {stage.label}
              </div>
              <div style={{ marginTop: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: circleColor }}>
                {completed ? "Completed" : current ? "Current" : "Pending"}
              </div>
            </li>
          );
        })}
      </ol>

      <div
        data-testid="bondready-lifecycle-status-summary"
        style={{
          ...br.gridAuto,
          marginTop: 18,
          paddingTop: 14,
          borderTop: "1px solid #2a3d52",
        }}
      >
        <Field label="Current stage">{currentStageLabel}</Field>
        <Field label="Current owner">
          {canonical.currentOwner ? ownerPresentation(canonical.currentOwner) : structuredOwnerPresentation(opportunity)}
        </Field>
        <Field label="Next action">
          <span style={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.45 }}>
            {nextActionPresentation(canonical.nextAction, opportunity.subjectName)}
          </span>
        </Field>
        <Field label="Due date">{dueLabel(canonical.nextActionDueAt)}</Field>
      </div>
    </Card>
  );
}

function nextWorkflowStatuses(opportunity: ApiRecord, gate1?: ApiRecord): string[] {
  const current = opportunity.workflowStatus;
  const transitions: Record<string, string[]> = {
    GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH: ["CONSTRUCTPAY_OUTREACH_STARTED"],
    CONSTRUCTPAY_OUTREACH_STARTED: ["TRADE_PARTNER_REVIEWING"],
    TRADE_PARTNER_REVIEWING: ["TRADE_PARTNER_APPROVED_INTRODUCTION"],
    TRADE_PARTNER_APPROVED_INTRODUCTION: ["INTRODUCED_TO_EXTERNAL_PARTNER"],
    INTRODUCED_TO_EXTERNAL_PARTNER: ["PARTNER_REVIEWING"],
    PARTNER_REVIEWING: ["DOCS_REQUESTED"],
    DOCS_REQUESTED: ["TERMS_BOND_PATH_RECEIVED"],
    TERMS_BOND_PATH_RECEIVED: ["CLOSED"],
  };
  const next = current === "NOT_REVIEWED"
    ? opportunity.originType === "DIRECT_CUSTOMER"
      ? ["CONSTRUCTPAY_OUTREACH_STARTED"]
      : gate1?.decision === "APPROVED"
        ? ["GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH"]
        : []
    : transitions[current] || [];
  return ["CLOSED", "DECLINED"].includes(current) ? [] : [...next, "DECLINED"];
}

function canMutateLifecycle(viewerRole: any): boolean {
  // Partners use governed partner actions only (information request / review).
  // ConstructPay operators retain internal lifecycle mutation controls.
  return viewerRole === "CONSTRUCTPAY_OPERATOR";
}

function canMutateSupportRequest(viewerRole: any): boolean {
  return viewerRole === "CONSTRUCTPAY_OPERATOR";
}

function ActionCenter({ opportunity, gate1, gate2, onSaved }: { opportunity: ApiRecord; gate1?: ApiRecord; gate2: ApiRecord; onSaved: () => Promise<void> }) {
  const allowed = canMutateLifecycle(opportunity.viewerRole);
  const choices = allowed ? nextWorkflowStatuses(opportunity, gate1) : [];
  const [workflowStatus, setWorkflowStatus] = useState(choices[0] || "");
  const [owner, setOwner] = useState(opportunity.currentOwner || "");
  const [nextAction, setNextAction] = useState(opportunity.nextAction || "");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const introductionLocked = workflowStatus === "INTRODUCED_TO_EXTERNAL_PARTNER" && (
    gate2.state !== "AUTHORIZED" || opportunity.dataQualityStatus === "CONFLICT"
  );

  useEffect(() => {
    if (!allowed) return;
    setWorkflowStatus(choices[0] || "");
    setOwner(opportunity.currentOwner || "");
    const presented = nextActionPresentation(opportunity.nextAction, opportunity.subjectName);
    setNextAction(presented === UNKNOWN ? (opportunity.nextAction || "") : presented);
    setDue("");
    setNote("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity.id, opportunity.workflowStatus, opportunity.updatedAt, opportunity.nextAction, allowed]);

  if (!allowed) {
    return null;
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: ApiRecord = {
      workflowStatus,
      currentOwner: owner.trim(),
      nextAction: nextAction.trim(),
    };
    optionalInstant(payload, "nextActionDueAt", due);
    optional(payload, "note", note);
    try {
      setError(null);
      await (bondReadyAPI as any).updateWorkflow(opportunity.id, payload);
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <Card
      title="Update lifecycle"
      subtitle="Operational controls to advance, decline, or revise the next step. Current status is shown in the tracker above."
      badge={
        <Pill tone="amber">
          {opportunity.viewerRole === "CONSTRUCTPAY_OPERATOR"
            ? "ConstructPay operator controls"
            : "Controls"}
        </Pill>
      }
      testId="bondready-action-center"
      style={br.sectionPad}
    >
      {choices.length ? (
        <details style={{ marginTop: 0 }}>
          <summary style={{ cursor: "pointer", color: "#7bb6ff", fontWeight: 700 }}>Update status / next action</summary>
          <form onSubmit={submit} style={{ ...br.form, gridTemplateColumns: "1fr" }}>
            <label style={{ color: "#9fb0c4", fontSize: 12 }}>
              Next lifecycle state
              <select aria-label="Next lifecycle state" value={workflowStatus} onChange={(event) => setWorkflowStatus(event.target.value)} style={br.field}>
                {choices.map((status) => (
                  <React.Fragment key={status}>
                    <option value={status}>{workflowLabel(status)}</option>
                  </React.Fragment>
                ))}
              </select>
            </label>
            <Input label="Lifecycle owner" value={owner} onChange={setOwner} required />
            <label style={{ color: "#9fb0c4", fontSize: 12 }}>
              Lifecycle next action
              <textarea
                aria-label="Lifecycle next action"
                required
                value={nextAction}
                onChange={(event) => setNextAction(event.target.value)}
                rows={3}
                style={{ ...br.field, resize: "vertical" as const, minHeight: 72, whiteSpace: "pre-wrap" as const }}
              />
            </label>
            <OptionalDueInput label="Lifecycle due (optional)" value={due} onChange={setDue} />
            <Input label="Lifecycle note (optional)" value={note} onChange={setNote} />
            <button type="submit" disabled={introductionLocked} style={{ ...br.btn, ...br.btnPrimary }}>Record lifecycle transition</button>
            {introductionLocked && <div role="status" style={{ color: "#ff7b7b" }}>External introduction is locked until contractor disclosure authorization is granted and source conflict is resolved.</div>}
            {error && <div role="alert" style={{ color: "#ff7b7b" }}>{error}</div>}
          </form>
        </details>
      ) : <p style={br.muted}>No lifecycle transition is currently available.</p>}
    </Card>
  );
}

function AttentionItems({
  opportunity,
  gate2,
  requests,
  opportunityType,
}: {
  opportunity: ApiRecord;
  gate2: ApiRecord;
  requests: ApiRecord[];
  opportunityType: any;
}) {
  const viewerRole = opportunity.viewerRole;
  const isContractorOwner = viewerRole === "OWNER";
  const isPartner = viewerRole === "AUTHORIZED_PARTNER";
  const canonical = canonicalOwnerNextAction({ ...opportunity, requests });
  const openInfoRequests = requests.filter(
    (item) => item.requestType === "BOND" && item.stage === "INFORMATION_NEEDED"
  );
  const responseReadyRequests = requests.filter(
    (item) => item.requestType === "BOND" && item.stage === "RESPONSE_PROVIDED"
  );
  const actionable: Array<{ key: string; primary: string; secondary?: string; cta?: string }> = [];
  const partnerNameRaw = opportunity.partnerDisplayName || opportunity.authorizedPartnerDisplayName;
  const cleanedPartner = partnerNameRaw ? customerFacingPartnerDisplayName(partnerNameRaw) : null;
  const partnerLead =
    cleanedPartner && cleanedPartner !== "Configured partner"
      ? `${cleanedPartner} requested information`
      : "Partner requested information";
  const contractorName = opportunity.subjectName
    ? String(opportunity.subjectName).trim()
    : "Contractor";

  openInfoRequests.forEach((request) => {
    const outstanding = humanizeEmbeddedInternalIdentifiers(request.outstandingInformation || "");
    const itemLabel = outstanding
      ? String(outstanding).replace(/^Partner information request:\s*/i, "").replace(/\s*\[.*?\]\s*/g, "").trim()
      : "Requested information";
    actionable.push({
      key: `info-${request.id}`,
      primary: partnerLead,
      secondary: itemLabel || "Review and respond to the open information request",
      cta: "Review request",
    });
  });

  if (isPartner && responseReadyRequests.length > 0) {
    responseReadyRequests.forEach((request) => {
      actionable.push({
        key: `review-${request.id}`,
        primary: "Needs your review",
        secondary: `${contractorName} shared the requested information.`,
        cta: "Review response",
      });
    });
  } else if (isPartner && /review/i.test(String(canonical.nextAction || ""))) {
    actionable.push({
      key: "canonical-review",
      primary: "Needs your review",
      secondary: nextActionPresentation(canonical.nextAction, opportunity.subjectName),
      cta: "Review response",
    });
  }

  const issues: string[] = [];
  const quality = opportunity.dataQualityStatus || "SOURCE_PENDING";
  if (quality === "CONFLICT") issues.push("Source conflict");
  else if (quality === "STALE") issues.push("Snapshot recency review required");
  else if (quality === "SOURCE_PENDING") issues.push("Source verification required");
  else if (quality !== "CURRENT") issues.push("Unrecognized source quality status");
  if (!canonical.currentOwner) issues.push("Missing owner");
  if (!canonical.nextAction) issues.push("Missing next action");
  const canCreateBond = opportunityType === "BOND" || opportunityType === "BOTH";
  const canCreateFunding = opportunityType === "FUNDING" || opportunityType === "BOTH";
  const hasBond = requests.some((item) => item.requestType === "BOND");
  const hasFunding = requests.some((item) => item.requestType === "FUNDING");
  if (canCreateBond && !hasBond) issues.push("Bond request not yet created");
  if (canCreateFunding && !hasFunding) issues.push("Working-capital request not yet created");
  if (!isContractorOwner && !isPartner && gate2.state !== "AUTHORIZED") {
    issues.push("External sharing not yet authorized");
  }
  const hasContractorOutstandingAction = openInfoRequests.length > 0;
  const hasPartnerReviewAction = isPartner && actionable.length > 0;
  const showDueSecondary = (hasContractorOutstandingAction || hasPartnerReviewAction)
    && !canonical.nextActionDueAt
    && !opportunity.nextActionDueAt;

  return (
    <Card
      title="Needs attention"
      subtitle={isContractorOwner
        ? "Actions that require your response."
        : isPartner
          ? "Actions that require partner review."
          : "Open blockers that affect broker next steps."}
      badge={<Pill tone="amber">Priority</Pill>}
      testId="bondready-attention"
      style={br.sectionPad}
    >
      {(isContractorOwner && hasContractorOutstandingAction) || hasPartnerReviewAction ? (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }} data-testid="bondready-attention-info-actions">
          {actionable.map((item) => (
            <li key={item.key} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: "#edf5ff" }}>{item.primary}</div>
              {item.secondary ? (
                <div style={{ color: "#c8d4e4", fontSize: 13, marginTop: 2 }}>{item.secondary}</div>
              ) : null}
              {item.cta ? (
                <div style={{ color: "#7bb6ff", fontSize: 12, marginTop: 4 }}>{item.cta}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {isContractorOwner && !hasContractorOutstandingAction && issues.length === 0 ? (
        <p style={br.muted} data-testid="bondready-attention-clear">
          No open actions require your response right now.
        </p>
      ) : null}
      {isPartner && !hasPartnerReviewAction && issues.length === 0 ? (
        <p style={br.muted} data-testid="bondready-attention-partner-clear">
          No open partner review actions right now.
        </p>
      ) : null}
      {!isContractorOwner && !isPartner && issues.length ? (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>{issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
      ) : null}
      {!isContractorOwner && !isPartner && !issues.length ? (
        <p style={br.muted}>No open blockers on the persisted record.</p>
      ) : null}
      {showDueSecondary ? (
        <p style={{ ...br.muted, marginTop: 10, fontSize: 12 }}>No due date set</p>
      ) : null}
      {isContractorOwner && !hasContractorOutstandingAction && issues.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>{issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
      ) : null}
    </Card>
  );
}

function Gate1Form({ opportunity, onSaved }: { opportunity: ApiRecord; onSaved: () => Promise<void> }) {
  const [decision, setDecision] = useState("APPROVED");
  const [owner, setOwner] = useState(opportunity.currentOwner || "");
  const [nextAction, setNextAction] = useState(opportunity.nextAction || "");
  const [due, setDue] = useState("");
  const [evidence, setEvidence] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: ApiRecord = { decision, currentOwner: owner.trim(), nextAction: nextAction.trim() };
    optionalInstant(payload, "nextActionDueAt", due);
    optional(payload, "sourceEvidenceReference", evidence);
    optional(payload, "note", note);
    try {
      setError(null);
      await (bondReadyAPI as any).decideGate1(opportunity.id, payload);
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <form onSubmit={submit} style={br.form} data-testid="bondready-gate1-form">
      <label style={{ color: "#9fb0c4", fontSize: 12 }}>
        Decision
        <select aria-label="Gate 1 decision" value={decision} onChange={(event) => setDecision(event.target.value)} style={br.field}>
          <option value="APPROVED">Approved</option>
          <option value="DENIED">Denied</option>
        </select>
      </label>
      <Input label="Gate 1 current owner" value={owner} onChange={setOwner} required />
      <Input label="Gate 1 next action" value={nextAction} onChange={setNextAction} required />
      <Input label="Gate 1 due (optional)" type="datetime-local" value={due} onChange={setDue} />
      <Input label="Gate 1 evidence reference (optional)" value={evidence} onChange={setEvidence} />
      <Input label="Gate 1 note (optional)" value={note} onChange={setNote} />
      <button type="submit" style={{ ...br.btn, ...br.btnPrimary }}>Record Gate 1</button>
      {error && <div role="alert" style={{ color: "#ff7b7b" }}>{error}</div>}
    </form>
  );
}

function Gate2Form({ opportunityId, onSaved }: { opportunityId: string; onSaved: () => Promise<void> }) {
  const [scope, setScope] = useState("");
  const [version, setVersion] = useState("");
  const [reference, setReference] = useState("");
  const [evidence, setEvidence] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: ApiRecord = {
      state: "AUTHORIZED",
      approvedScope: list(scope),
      releasePackageVersion: version.trim(),
      releasePackageReference: reference.trim(),
      sourceEvidenceReference: evidence.trim(),
    };
    const excluded = list(exclusions);
    if (excluded.length) payload.exclusions = excluded;
    optional(payload, "note", note);
    try {
      setError(null);
      await (bondReadyAPI as any).decideGate2(opportunityId, payload);
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <form onSubmit={submit} style={br.form} data-testid="bondready-gate2-form">
      <Input label="Approved scope" value={scope} onChange={setScope} required />
      <Input label="Package version" value={version} onChange={setVersion} required />
      <Input label="Package reference" value={reference} onChange={setReference} required />
      <Input label="Evidence reference" value={evidence} onChange={setEvidence} required />
      <Input label="Exclusions (optional)" value={exclusions} onChange={setExclusions} />
      <Input label="Note (optional)" value={note} onChange={setNote} />
      <button type="submit" style={{ ...br.btn, ...br.btnPrimary }}>Authorize Gate 2</button>
      {error && <div role="alert" style={{ color: "#ff7b7b" }}>{error}</div>}
    </form>
  );
}

function SupportRequestForm({ opportunityId, requestType, disabled, onSaved }: { opportunityId: string; requestType: "BOND" | "FUNDING"; disabled: boolean; onSaved: () => Promise<void> }) {
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [owner, setOwner] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [due, setDue] = useState("");
  const [outstanding, setOutstanding] = useState("");
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState("");
  const [scenarioId, setScenarioId] = useState("");
  const [response, setResponse] = useState<ApiRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: ApiRecord = { status: status.trim(), stage: stage.trim() };
    optional(payload, "currentOwner", owner);
    optional(payload, "nextAction", nextAction);
    optionalInstant(payload, "nextActionDueAt", due);
    optional(payload, "outstandingInformation", outstanding);
    if (amount.trim()) payload.amount = Number(amount);
    optional(payload, "details", details);
    if (scenarioId.trim()) payload.scenarioAssumptionId = Number(scenarioId);
    try {
      setError(null);
      const method = requestType === "BOND"
        ? (bondReadyAPI as any).createBondRequest
        : (bondReadyAPI as any).createFundingRequest;
      const result = await method(opportunityId, payload);
      setResponse(result.data);
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const title = requestType === "BOND" ? "Bond" : "Funding";
  return (
    <form onSubmit={submit} style={br.form} data-testid={`bondready-${requestType.toLowerCase()}-request-form`}>
      <Input label="Status" value={status} onChange={setStatus} required />
      <Input label="Stage" value={stage} onChange={setStage} required />
      <Input label="Current owner (optional)" value={owner} onChange={setOwner} />
      <Input label="Next action (optional)" value={nextAction} onChange={setNextAction} />
      <Input label="Next action due (optional)" type="datetime-local" value={due} onChange={setDue} />
      <Input label="Outstanding information (optional)" value={outstanding} onChange={setOutstanding} />
      <Input label="Amount (optional)" type="number" value={amount} onChange={setAmount} />
      <Input label="Details (optional)" value={details} onChange={setDetails} />
      <Input label="Existing scenario assumption ID (optional)" type="number" value={scenarioId} onChange={setScenarioId} />
      <button type="submit" disabled={disabled} style={{ ...br.btn, ...br.btnPrimary }}>Create {title} request</button>
      {disabled && <div style={br.muted}>A persisted {title.toLowerCase()} request already exists.</div>}
      {error && <div role="alert" style={{ color: "#ff7b7b" }}>{error}</div>}
      {response && <RequestCard request={response} />}
    </form>
  );
}

function RequestUpdateForm({ opportunityId, request, onSaved }: { opportunityId: string; request: ApiRecord; onSaved: () => Promise<void> }) {
  const [status, setStatus] = useState(request.status || "");
  const [stage, setStage] = useState(request.stage || "");
  const [owner, setOwner] = useState(request.currentOwner || "");
  const [nextAction, setNextAction] = useState(request.nextAction || "");
  const [due, setDue] = useState("");
  const [outstanding, setOutstanding] = useState(request.outstandingInformation || "");
  const [amount, setAmount] = useState(request.amount === null || request.amount === undefined ? "" : String(request.amount));
  const [details, setDetails] = useState(request.details || "");
  const [scenarioId, setScenarioId] = useState(request.scenarioAssumptionId === null || request.scenarioAssumptionId === undefined ? "" : String(request.scenarioAssumptionId));
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: ApiRecord = {
      status: status.trim(),
      stage: stage.trim(),
      currentOwner: owner.trim(),
      nextAction: nextAction.trim(),
    };
    optionalInstant(payload, "nextActionDueAt", due);
    optional(payload, "outstandingInformation", outstanding);
    if (amount.trim()) payload.amount = Number(amount);
    optional(payload, "details", details);
    if (scenarioId.trim()) payload.scenarioAssumptionId = Number(scenarioId);
    try {
      setError(null);
      await (bondReadyAPI as any).updateSupportRequest(opportunityId, request.id, payload);
      await onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <details style={{ marginTop: "0.75rem" }}>
      <summary>
        {request.requestType === "BOND"
          ? "Update bond request"
          : request.requestType === "FUNDING"
            ? "Update working-capital request"
            : "Update request"}
      </summary>
      <form onSubmit={submit} style={br.form}>
        <Input label="Update request status" value={status} onChange={setStatus} required />
        <Input label="Update request stage" value={stage} onChange={setStage} required />
        <Input label="Update request owner" value={owner} onChange={setOwner} required />
        <Input label="Update request next action" value={nextAction} onChange={setNextAction} required />
        <Input label="Update request due (optional)" type="datetime-local" value={due} onChange={setDue} />
        <Input label="Update outstanding information (optional)" value={outstanding} onChange={setOutstanding} />
        <Input label="Update amount (optional)" type="number" value={amount} onChange={setAmount} />
        <Input label="Update details (optional)" value={details} onChange={setDetails} />
        <Input label="Update scenario assumption ID (optional)" type="number" value={scenarioId} onChange={setScenarioId} />
        <button type="submit" style={{ ...br.btn, ...br.btnPrimary }}>Save request update</button>
        {error && <div role="alert" style={{ color: "#ff7b7b" }}>{error}</div>}
      </form>
    </details>
  );
}

function FinancialReadiness({
  opportunity,
  ownerResolvedAsOf,
}: {
  opportunity: ApiRecord;
  ownerResolvedAsOf?: string | null;
}) {
  const readiness = readinessFromSnapshot(opportunity.sourceSnapshotContext);
  const financialAsOf = structuredFinancialAsOf(opportunity);
  const bankabilityDisplay = readiness.bankability
    ? (/^(present|available)$/i.test(readiness.bankability) ? readiness.bankability : "present")
    : null;
  const throughLabel = structuredFinancialThroughLabel(
    financialAsOf,
    opportunity.dataQualityStatus,
    { viewerRole: opportunity.viewerRole, ownerResolvedAsOf }
  );
  const hasAny = !!(
    financialAsOf
    || readiness.financialSnapshotDate
    || opportunity.sourceSnapshotAsOf
    || ownerResolvedAsOf
    || opportunity.dataQualityStatus === "CURRENT"
    || opportunity.dataQualityStatus === "STALE"
    || readiness.arAsOf
    || readiness.apAsOf
    || readiness.wipAsOf
    || bankabilityDisplay
    || opportunity.sourceConfidence
    || opportunity.dataQualityStatus
  );
  return (
    <Card
      title="Financial readiness"
      subtitle="Source-backed financial dates and quality signals. No fabricated scores."
      badge={<Pill tone="blue">Source-backed</Pill>}
      testId="bondready-readiness"
      style={br.sectionPad}
    >
      {!hasAny ? (
        <div style={br.muted}>No financial readiness indicators are available on this opportunity yet.</div>
      ) : (
        <div style={br.gridAuto}>
          <Field label="Structured financial data through">{throughLabel}</Field>
          {readiness.arAsOf && <Field label="AR as of">{humanDate(readiness.arAsOf)}</Field>}
          {readiness.wipAsOf && <Field label="WIP as of">{humanDate(readiness.wipAsOf)}</Field>}
          {readiness.apAsOf && <Field label="AP as of">{humanDate(readiness.apAsOf)}</Field>}
          {bankabilityDisplay && <Field label="Bankability factor">{bankabilityDisplay}</Field>}
          {opportunity.sourceConfidence && <Field label="Source confidence">{value(opportunity.sourceConfidence)}</Field>}
          {opportunity.dataQualityStatus && <Field label="Data quality">{qualityLabel(opportunity.dataQualityStatus)}</Field>}
        </div>
      )}
      <DataQualityNotice status={opportunity.dataQualityStatus} />
    </Card>
  );
}

function RequestsSection({
  opportunity,
  requests,
  canCreateBond,
  canCreateFunding,
  onSaved,
}: {
  opportunity: ApiRecord;
  requests: ApiRecord[];
  canCreateBond: boolean;
  canCreateFunding: boolean;
  onSaved: () => Promise<void>;
}) {
  const bond = requests.find((item) => item.requestType === "BOND");
  const funding = requests.find((item) => item.requestType === "FUNDING");
  const canManageRequests = opportunity.viewerRole === "OWNER"
    || opportunity.viewerRole === "CONSTRUCTPAY_OPERATOR";
  // Backend enforces one request per type (UNIQUE opportunity_id, request_type).
  const showCreateBond = canManageRequests && canCreateBond && !bond;
  const showCreateFunding = canManageRequests && canCreateFunding && !funding;
  return (
    <Card
      title="Bond and funding requests"
      subtitle="What the contractor is asking for, and what is still outstanding."
      badge={<Pill tone="purple">Requests</Pill>}
      testId="bondready-requests"
      style={br.sectionPad}
    >
      {bond ? (
        <RequestCard
          request={bond}
          opportunityId={opportunity.id}
          viewerRole={opportunity.viewerRole}
          onSaved={onSaved}
        />
      ) : (
        <div style={br.ticket}>
          <h3 style={{ margin: "0 0 0.4rem", fontSize: "0.9rem" }}>Bond request</h3>
          <div style={br.muted}>{requestCountLabel(0)}</div>
        </div>
      )}

      {funding ? (
        <RequestCard
          request={funding}
          opportunityId={opportunity.id}
          viewerRole={opportunity.viewerRole}
          onSaved={onSaved}
        />
      ) : (
        <div style={br.ticket}>
          <h3 style={{ margin: "0 0 0.4rem", fontSize: "0.9rem" }}>Working-capital request</h3>
          <div style={br.muted}>{requestCountLabel(0)}</div>
        </div>
      )}

      {(showCreateBond || showCreateFunding) ? (
        <details style={{ marginTop: "0.85rem" }} open={false}>
          <summary style={{ cursor: "pointer", color: "#7bb6ff", fontWeight: 700 }}>Record a new request</summary>
          {showCreateBond && (
            <SupportRequestForm
              opportunityId={opportunity.id}
              requestType="BOND"
              disabled={false}
              onSaved={onSaved}
            />
          )}
          {showCreateFunding && (
            <SupportRequestForm
              opportunityId={opportunity.id}
              requestType="FUNDING"
              disabled={false}
              onSaved={onSaved}
            />
          )}
        </details>
      ) : canManageRequests && (bond || funding) ? (
        <p style={{ ...br.muted, marginTop: "0.85rem" }} data-testid="bondready-request-create-locked">
          {bond && funding
            ? "A bond request and working-capital request are already recorded for this opportunity. Use Update on each request above ? BondReady V1 supports one active request per type."
            : bond
              ? "A bond request is already recorded for this opportunity. Use Update on that request above ? BondReady V1 supports one active request per type."
              : "A working-capital request is already recorded for this opportunity. Use Update on that request above ? BondReady V1 supports one active request per type."}
        </p>
      ) : null}
    </Card>
  );
}

function partnerReviewCopy(viewerRole: any): string | null {
  if (viewerRole === "AUTHORIZED_PARTNER") {
    return "Holmes Murphy is authorized to review this opportunity in ConstructPay.";
  }
  if (viewerRole === "OWNER") return "You own this opportunity.";
  if (viewerRole === "CONSTRUCTPAY_OPERATOR") {
    return "ConstructPay operator may administer workflow state. Contractor disclosure authorization remains owner-only and fail-closed.";
  }
  return viewerAccessLabel(viewerRole);
}

function externalSharingHeadline(disclosureAllowed: boolean, viewerRole: any): string {
  if (disclosureAllowed) return "Contractor authorization granted";
  if (viewerRole === "AUTHORIZED_PARTNER") return "Awaiting contractor authorization";
  return "Contractor authorization required";
}

function GovernanceSection({
  opportunity,
  gate1,
  gate2,
  policy,
  onSaved,
}: {
  opportunity: ApiRecord;
  gate1?: ApiRecord;
  gate2: ApiRecord;
  policy: ApiRecord;
  onSaved: () => Promise<void>;
}) {
  const disclosureAllowed = !!policy.companySpecificExternalDisclosureAllowed;
  const isOwner = opportunity.viewerRole === "OWNER";
  const reviewCopy = partnerReviewCopy(opportunity.viewerRole);
  const sharingHeadline = externalSharingHeadline(disclosureAllowed, opportunity.viewerRole);
  const canAuthorizeGate2 =
    isOwner && (gate2.state === "INITIAL" || gate2.state === "PENDING");
  const canAuthorizeGate1 =
    isOwner &&
    opportunity.originType === "GC_SPONSORED" &&
    opportunity.workflowStatus === "NOT_REVIEWED" &&
    !gate1;

  return (
    <Card
      title="Authorization & Sharing"
      subtitle="Workspace visibility is separate from disclosure authority."
      badge={
        <Pill tone={disclosureAllowed ? "green" : "lock"}>
          {disclosureAllowed ? "Sharing authorized" : "Contractor authorization required"}
        </Pill>
      }
      testId="bondready-governance"
      style={br.sectionPad}
    >
      <div style={br.gridAuto}>
        {reviewCopy && (
          <div style={br.calloutGreen} data-testid="bondready-authorized-to-review">
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9fb0c4", marginBottom: 4 }}>
              Authorized to review
            </div>
            <strong>{reviewCopy}</strong>
          </div>
        )}
        <div
          style={disclosureAllowed ? br.calloutGreen : br.calloutRed}
          data-testid="bondready-external-sharing"
        >
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9fb0c4", marginBottom: 4 }}>
            External sharing
          </div>
          <strong>{sharingHeadline}</strong>
          <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.45 }}>
            {disclosureAllowed
              ? "Company-specific disclosure may proceed only within the approved scope."
              : "Company-specific information remains locked until the contractor authorizes disclosure."}
          </p>
        </div>
      </div>

      <div style={{ ...br.gridAuto, marginTop: "0.85rem" }}>
        <Field label="Workspace access">Authorized to review this opportunity</Field>
      </div>

      {canAuthorizeGate1 && <Gate1Form opportunity={opportunity} onSaved={onSaved} />}
      {canAuthorizeGate2 && <Gate2Form opportunityId={opportunity.id} onSaved={onSaved} />}
    </Card>
  );
}

function TimelineSection({ events }: { events: ApiRecord[] }) {
  const ordered = [...events]
    .filter(isCustomerFacingTimelineEvent)
    .sort((a, b) => {
      const aTime = Date.parse(String(a.occurredAt || "")) || 0;
      const bTime = Date.parse(String(b.occurredAt || "")) || 0;
      return aTime - bTime;
    });
  return (
    <Card
      title="Workflow timeline"
      subtitle="Source-backed history of what has happened on this opportunity."
      badge={<Pill tone="blue">Timeline</Pill>}
      testId="bondready-timeline"
      style={br.sectionPad}
    >
      {ordered.length ? ordered.map((event) => {
        const presented = activityPresentation(event);
        return (
          <div key={event.id} style={{ ...br.ticket, display: "grid", gridTemplateColumns: "7.5rem 1fr", gap: 12 }}>
            <div style={{ color: "#9fb0c4", fontSize: 12, fontWeight: 700 }}>{humanDate(event.occurredAt)}</div>
            <div>
              <div style={{ color: "#edf5ff", fontSize: 13, fontWeight: 700 }}>{presented.headline}</div>
              {presented.detail ? (
                <div style={{ ...br.muted, marginTop: 4 }}>{presented.detail}</div>
              ) : null}
            </div>
          </div>
        );
      }) : <div style={br.muted}>No activity has been recorded yet.</div>}
    </Card>
  );
}

function canViewAuditSourceDetails(viewerRole: any): boolean {
  // Operator-only technical lineage. Contractors and surety partners see business workflow only.
  return viewerRole === "CONSTRUCTPAY_OPERATOR";
}

function AuditSourceDetails({
  opportunity,
  events,
  gate1,
  gate2,
  policy,
}: {
  opportunity: ApiRecord;
  events: ApiRecord[];
  gate1?: ApiRecord;
  gate2: ApiRecord;
  policy: ApiRecord;
}) {
  return (
    <Card
      title="Audit & source details"
      subtitle="Technical lineage and identifiers for ConstructPay operations and audit support. Not shown to surety partners."
      badge={<Pill tone="lock">Operator</Pill>}
      testId="bondready-audit"
      style={br.sectionPad}
    >
      <details open={false}>
        <summary id="bondready-audit" style={{ cursor: "pointer", color: "#7bb6ff", fontWeight: 700, fontSize: "1.05rem" }}>
          Expand audit &amp; source details
        </summary>
        <div style={{ ...br.gridAuto, marginTop: 12 }}>
          <Field label="Opportunity ID">{value(opportunity.id)}</Field>
          <Field label="Subject source system">{value(opportunity.subjectSourceSystem)}</Field>
          <Field label="Subject source record ID">{value(opportunity.subjectSourceRecordId)}</Field>
          <Field label="Queue source ID">{value(opportunity.introductionQueueSourceId)}</Field>
          <Field label="Source system">{value(opportunity.sourceSystem)}</Field>
          <Field label="Source record ID">{value(opportunity.sourceRecordId)}</Field>
          <Field label="Source batch ID">{value(opportunity.sourceBatchId)}</Field>
          <Field label="Source snapshot">{value(opportunity.sourceSnapshotContext)}</Field>
          <Field label="Source snapshot as of">{value(opportunity.sourceSnapshotAsOf)}</Field>
          <Field label="Source confidence">{value(opportunity.sourceConfidence)}</Field>
          <Field label="Data quality">{value(opportunity.dataQualityStatus)}</Field>
          {(() => {
            const historical = readinessFromSnapshot(opportunity.sourceSnapshotContext);
            if (!historical.constructScore && !historical.band) return null;
            return (
              <>
                {historical.constructScore && (
                  <Field label="Historical ConstructScore">{historical.constructScore}</Field>
                )}
                {historical.band && (
                  <Field label="Historical readiness band">{historical.band}</Field>
                )}
                <Field label="Historical score note">
                  Prior ConstructScore lineage only. Does not reflect the current structured financial refresh date.
                </Field>
              </>
            );
          })()}
          <Field label="Type">{value(opportunity.opportunityType)}</Field>
          <Field label="Origin">{value(opportunity.originType)}</Field>
          <Field label="Workflow state">{value(opportunity.workflowStatus)}</Field>
          <Field label="Viewer role">{value(opportunity.viewerRole)}</Field>
          <Field label="Policy reason">{disclosureReasonLabel(policy.reason)}</Field>
          <Field label="Required authorization">{value(policy.requiredAuthorizationType)}</Field>
          {policy.manualReleaseRequired && (
            <Field label="Release mode">Manual release only ? this screen does not email, webhook, or automatically share company-specific data.</Field>
          )}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <h3 style={{ fontSize: "0.9rem" }}>Authorization lineage</h3>
          <p style={{ fontSize: 12, color: "#9fb0c4", lineHeight: 1.45 }}>
            Approval authorizes ConstructPay contact only; it does not authorize company-specific external disclosure.
          </p>
          <h4 style={{ fontSize: "0.85rem", marginTop: 12 }}>Gate 1 ? ConstructPay contact</h4>
          <AuthorizationCard authorization={gate1} />
          <h4 style={{ fontSize: "0.85rem", marginTop: 12 }}>Gate 2 ? bounded disclosure authority</h4>
          <AuthorizationCard authorization={gate2} />
        </div>
        <div style={{ marginTop: "1rem" }}>
          <h3 style={{ fontSize: "0.9rem" }}>Persisted timeline evidence</h3>
          {events.length ? events.map((event) => (
            <React.Fragment key={`audit-${event.id}`}>
              <div style={br.ticket}>
                <div style={br.gridAuto}>
                  <Field label="Event">{value(event.eventType)}</Field>
                  <Field label="Occurred">{value(event.occurredAt)}</Field>
                  <Field label="Action">{value(event.action)}</Field>
                  <Field label="Resulting state">{value(event.resultingState || event.newStatus)}</Field>
                  <Field label="Request type">{value(event.requestType)}</Field>
                  <Field label="Note">{value(event.note)}</Field>
                  <Field label="Evidence reference">{value(event.sourceEvidenceReference)}</Field>
                </div>
              </div>
            </React.Fragment>
          )) : <div>{UNKNOWN}</div>}
        </div>
      </details>
    </Card>
  );
}

export default function BondReadyOpportunity({ opportunityId: suppliedId }: { opportunityId?: string }) {
  const opportunityId = suppliedId || window.location.pathname.split("/").filter(Boolean).pop() || "";
  const validOpportunityId = OPPORTUNITY_ID_PATTERN.test(opportunityId);
  const [opportunity, setOpportunity] = useState<ApiRecord | null>(null);
  const [gate2, setGate2] = useState<ApiRecord | null>(null);
  const [policy, setPolicy] = useState<ApiRecord | null>(null);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [ownerResolvedAsOf, setOwnerResolvedAsOf] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      if (!validOpportunityId) {
        setError({
          title: "Invalid BondReady link",
          message: "The opportunity reference is malformed. No request was made.",
        });
        return;
      }
      const [opportunityResponse, gate2Response, policyResponse] = await Promise.all([
        (bondReadyAPI as any).getOpportunity(opportunityId),
        (bondReadyAPI as any).getGate2(opportunityId),
        (bondReadyAPI as any).getDisclosurePolicy(opportunityId),
      ]);
      setOpportunity(opportunityResponse.data);
      setGate2(gate2Response.data);
      setPolicy(policyResponse.data);
    } catch (caught) {
      setError(opportunityLoadFailure(caught));
    }
  }, [opportunityId, validOpportunityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    async function resolveOwnerAsOf() {
      if (!opportunity) {
        setOwnerResolvedAsOf(null);
        return;
      }
      const attached = structuredFinancialAsOf(opportunity);
      if (attached) {
        setOwnerResolvedAsOf(null);
        return;
      }
      // Owner/operator only: reuse Scenario Modeling actuals as-of for the same org.
      // Partners must not receive a broader financial projection here.
      if (opportunity.viewerRole !== "OWNER" && opportunity.viewerRole !== "CONSTRUCTPAY_OPERATOR") {
        setOwnerResolvedAsOf(null);
        return;
      }
      if (opportunity.dataQualityStatus !== "CURRENT" && opportunity.dataQualityStatus !== "STALE") {
        setOwnerResolvedAsOf(null);
        return;
      }
      try {
        const actuals = await getActuals();
        if (!cancelled) {
          setOwnerResolvedAsOf(actuals?.asOfDate ? String(actuals.asOfDate) : null);
        }
      } catch {
        if (!cancelled) setOwnerResolvedAsOf(null);
      }
    }
    resolveOwnerAsOf();
    return () => { cancelled = true; };
  }, [opportunity]);

  if (error) {
    return (
      <BondReadyShell
        activeNav="pipeline"
        title={error.title}
        subtitle="The authenticated BondReady destination could not be opened."
      >
        <div role="alert" data-testid="bondready-opportunity-load-error">
          {error.message}
        </div>
        <p style={{ ...br.muted, marginTop: 12 }}>Authorization remains fail-closed.</p>
        <a href="/bondready" style={{ ...br.btn, ...br.btnPrimary, marginTop: 12, display: "inline-flex" }}>
          Back to BondReady portfolio
        </a>
      </BondReadyShell>
    );
  }

  if (!opportunity || !gate2 || !policy) {
    return (
      <BondReadyShell
        activeNav="pipeline"
        title="Opportunity"
        subtitle="Loading persisted BondReady opportunity..."
      >
        Loading persisted BondReady opportunity...
      </BondReadyShell>
    );
  }

  const gate1 = (opportunity.authorizations || []).find((item: ApiRecord) => item.authorizationType === "GATE_1_OUTREACH");
  const requests: ApiRecord[] = opportunity.requests || [];
  const events: ApiRecord[] = opportunity.workflowEvents || [];
  const canCreateBond = opportunity.opportunityType === "BOND" || opportunity.opportunityType === "BOTH";
  const canCreateFunding = opportunity.opportunityType === "FUNDING" || opportunity.opportunityType === "BOTH";
  const isNestedGcRoiRoute = window.location.pathname.startsWith("/gc-roi/bondready/");
  const isOwnerWorkspace = opportunity.viewerRole === "OWNER";
  const backHref = isNestedGcRoiRoute
    ? "/gc-roi"
    : isOwnerWorkspace
      ? "/cfo-insights/bondready"
      : "/bondready";
  const backLabel = isNestedGcRoiRoute
    ? "Back to GC ROI"
    : isOwnerWorkspace
      ? "Back to BondReady"
      : "Back to BondReady pipeline";
  const accessLabel = viewerAccessLabel(opportunity.viewerRole);
  const disclosureAllowed = !!policy.companySpecificExternalDisclosureAllowed;
  const sharingHeadline = externalSharingHeadline(disclosureAllowed, opportunity.viewerRole);
  const shellTitle = opportunity.subjectName ? String(opportunity.subjectName) : "Opportunity";
  const shellSubtitle = `${opportunityRelationshipLabel(opportunity)} ? ${customerFacingCurrentStageLabel(opportunity)}`;
  const bondRequest = requests.find((item) => item.requestType === "BOND");
  const fundingRequest = requests.find((item) => item.requestType === "FUNDING");
  const financialAsOf = structuredFinancialAsOf(opportunity);
  const financialThroughLabel = structuredFinancialThroughLabel(
    financialAsOf,
    opportunity.dataQualityStatus,
    { viewerRole: opportunity.viewerRole, ownerResolvedAsOf }
  );
  const headerStageLabel = customerFacingCurrentStageLabel(opportunity);

  return (
    <div data-testid="bondready-opportunity-detail">
      <BondReadyShell
        activeNav="pipeline"
        title={shellTitle}
        subtitle={shellSubtitle}
        topActions={(
          <a href={backHref} style={br.btn}>{backLabel}</a>
        )}
      >
        <header style={br.banner} data-testid="bondready-broker-overview">
          <div>
            <h1 style={{ margin: "0 0 6px", fontSize: 22 }}>{value(opportunity.subjectName)}</h1>
            <div style={{ color: "#7bb6ff", fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
              {opportunityRelationshipLabel(opportunity)}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Pill tone="blue">{headerStageLabel}</Pill>
              <Pill>{originLabel(opportunity.originType)}</Pill>
              {accessLabel && <Pill tone="green">{accessLabel}</Pill>}
              <Pill tone={disclosureAllowed ? "green" : "lock"}>
                {disclosureAllowed ? "External sharing authorized" : sharingHeadline}
              </Pill>
            </div>
            {!disclosureAllowed && (
              <div style={{ ...br.callout, marginTop: 10, padding: "8px 10px" }}>
                Company-specific information remains locked until the contractor authorizes disclosure.
              </div>
            )}
            <div style={{ ...br.gridAuto, marginTop: 12 }}>
              {fundingRequest && (
                <Field label="Working Capital">
                  {(() => {
                    const detail = requestPrimaryDetailPresentation(fundingRequest);
                    return (
                      <>
                        {detail.amountLabel ? <div>{detail.amountLabel}</div> : null}
                        {detail.details ? (
                          <div style={{ fontSize: detail.amountLabel ? 12 : 14, color: detail.amountLabel ? "#9fb0c4" : "#c8d4e4", marginTop: detail.amountLabel ? 4 : 0, fontWeight: detail.amountLabel ? 400 : 600 }}>
                            {detail.details}
                          </div>
                        ) : null}
                        {!detail.amountLabel && !detail.details ? (
                          <div style={{ fontSize: 12, color: "#9fb0c4" }}>Amount not provided</div>
                        ) : null}
                      </>
                    );
                  })()}
                </Field>
              )}
              {bondRequest && (
                <Field label="Bond">
                  {(() => {
                    const detail = requestPrimaryDetailPresentation(bondRequest);
                    return (
                      <>
                        {detail.amountLabel ? <div>{detail.amountLabel}</div> : null}
                        {detail.details ? (
                          <div style={{ fontSize: detail.amountLabel ? 12 : 14, color: detail.amountLabel ? "#9fb0c4" : "#c8d4e4", marginTop: detail.amountLabel ? 4 : 0, fontWeight: detail.amountLabel ? 400 : 600 }}>
                            {detail.details}
                          </div>
                        ) : null}
                        {!detail.amountLabel && !detail.details ? (
                          <div style={{ fontSize: 12, color: "#9fb0c4" }}>Amount not provided</div>
                        ) : null}
                      </>
                    );
                  })()}
                </Field>
              )}
              <Field label="Structured financial data through">{financialThroughLabel}</Field>
              <Field label="Requests">{requestCountLabel(requests.length)}</Field>
            </div>
            <BondRequestProjectContext request={bondRequest} />
          </div>
        </header>

        <OpportunityLifecycleTracker opportunity={{ ...opportunity, requests }} />
        <TimelineSection events={events} />
        <ActionCenter opportunity={opportunity} gate1={gate1} gate2={gate2} onSaved={load} />
        <AttentionItems
          opportunity={opportunity}
          gate2={gate2}
          requests={requests}
          opportunityType={opportunity.opportunityType}
        />
        <FinancialReadiness opportunity={opportunity} ownerResolvedAsOf={ownerResolvedAsOf} />
        <RequestsSection
          opportunity={opportunity}
          requests={requests}
          canCreateBond={canCreateBond}
          canCreateFunding={canCreateFunding}
          onSaved={load}
        />
        <GovernanceSection
          opportunity={opportunity}
          gate1={gate1}
          gate2={gate2}
          policy={policy}
          onSaved={load}
        />
        {canViewAuditSourceDetails(opportunity.viewerRole) ? (
          <AuditSourceDetails
            opportunity={opportunity}
            events={events}
            gate1={gate1}
            gate2={gate2}
            policy={policy}
          />
        ) : null}
      </BondReadyShell>
    </div>
  );
}
